/**
 * KHR_lights_punctual support for model3d resources.
 *
 * Lights are read straight from the GLB JSON chunk so the action editor can list them
 * without a GLTFLoader parse or a mounted Three.js preview scene. `mapGltfLightsById()`
 * derives its keys from the same pass, so a light id stored in an action resolves back
 * to the matching THREE.Light in the preview.
 */

/**
 * Version of the derived light metadata cached on a model3d resource.
 *
 * Bump this whenever `uniqueLightId()` changes: cached lights carry ids produced by the
 * strategy of their day, so stale entries must be re-analysed instead of trusted.
 * v2 prefers a uniquely-used light definition name over the node name.
 */
export const H3D_LIGHTS_METADATA_VERSION = 2

const GLB_MAGIC = 0x46546c67
const JSON_CHUNK_TYPE = 0x4e4f534a
const LIGHTS_EXTENSION = 'KHR_lights_punctual'
const SUPPORTED_TYPES = new Set(['point', 'directional', 'spot'])
const TYPE_LABELS = { point: 'Point', directional: 'Directional', spot: 'Spot' }
// KHR_lights_punctual spot defaults, and the range the spec allows for both angles.
const DEFAULT_INNER_CONE_ANGLE = 0
const DEFAULT_OUTER_CONE_ANGLE = Math.PI / 4
const MAX_CONE_ANGLE = Math.PI / 2

/** Reads the JSON chunk of a GLB container without decoding any geometry. */
export function readGlbJson(source) {
  const buffer = toArrayBuffer(source)
  if (!buffer || buffer.byteLength < 12) return null

  const view = new DataView(buffer)
  if (view.getUint32(0, true) !== GLB_MAGIC) return null

  const totalLength = Math.min(view.getUint32(8, true), buffer.byteLength)
  let offset = 12
  while (offset + 8 <= totalLength) {
    const chunkLength = view.getUint32(offset, true)
    const chunkType = view.getUint32(offset + 4, true)
    const chunkStart = offset + 8
    if (chunkStart + chunkLength > totalLength) break
    if (chunkType === JSON_CHUNK_TYPE) {
      try {
        return JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, chunkStart, chunkLength)))
      } catch {
        return null
      }
    }
    offset = chunkStart + chunkLength
  }
  return null
}

/**
 * Lists the punctual lights of a parsed glTF document.
 *
 * One entry per *node* referencing a light, because a single light definition may be
 * instanced by several nodes and each instance is a separate light in the rendered scene.
 */
export function extractGltfLights(gltfJson) {
  const definitions = gltfJson?.extensions?.[LIGHTS_EXTENSION]?.lights
  if (!Array.isArray(definitions) || !definitions.length) return []

  const nodes = Array.isArray(gltfJson.nodes) ? gltfJson.nodes : []
  const instances = []
  nodes.forEach((node, nodeIndex) => {
    const lightIndex = node?.extensions?.[LIGHTS_EXTENSION]?.light
    if (!Number.isInteger(lightIndex)) return
    const definition = definitions[lightIndex]
    if (!definition || !SUPPORTED_TYPES.has(definition.type)) return
    instances.push({ node, nodeIndex, lightIndex, definition })
  })

  // Blender names the light *definition* (`left_light`) while the node keeps the object
  // name (`Light.001`), so the definition name is the better label — but only when a
  // single node instances it, otherwise it cannot tell the instances apart.
  const instanceCounts = new Map()
  for (const { lightIndex } of instances) {
    instanceCounts.set(lightIndex, (instanceCounts.get(lightIndex) ?? 0) + 1)
  }

  const usedIds = new Set()
  return instances.map(({ node, nodeIndex, lightIndex, definition }) => {
    const uniqueDefinitionName = instanceCounts.get(lightIndex) === 1 ? definition.name : ''
    const label = uniqueDefinitionName || node.name || definition.name || ''
    return {
      id: uniqueLightId(label, nodeIndex, usedIds),
      name: label || `Light ${lightIndex}`,
      type: definition.type,
      color: normalizeColor(definition.color),
      intensity: Number.isFinite(definition.intensity) ? definition.intensity : 1,
      // Spot only; the angles come from the glTF document, never from the THREE.SpotLight
      // the loader derives (its `angle`/`penumbra` are a lossy re-encoding of these two).
      ...(definition.type === 'spot' ? { spot: normalizeSpotCone(definition.spot) } : {}),
      nodeIndex,
      lightIndex,
    }
  })
}

/** Lists the punctual lights of a GLB buffer. */
export function extractGlbLights(source) {
  return extractGltfLights(readGlbJson(source))
}

/** Dropdown label, e.g. `Kitchen ceiling — Point`. */
export function formatH3dLightLabel(light) {
  if (!light) return ''
  return `${light.name} — ${TYPE_LABELS[light.type] ?? light.type}`
}

/**
 * Maps stable light ids onto the THREE.Light instances of a loaded glTF.
 *
 * Node names are not unique, so lights are matched through the loader's node
 * associations rather than `scene.getObjectByName()`.
 *
 * Each entry keeps the intensity the light was loaded with: preview actions scale
 * relative to it, so repeated updates never drift.
 *
 * @returns {Map<string, { light: import('three').Light, baseIntensity: number }>}
 */
export function mapGltfLightsById(gltf) {
  const map = new Map()
  if (!gltf?.scene) return map

  const lights = extractGltfLights(gltf.parser?.json)
  if (!lights.length) return map

  const idByNodeIndex = new Map(lights.map(light => [light.nodeIndex, light.id]))
  const associations = gltf.parser?.associations
  const unassociated = []
  gltf.scene.traverse(object => {
    if (!object.isLight) return
    const nodeIndex = associations?.get(object)?.nodes
    const id = Number.isInteger(nodeIndex) ? idByNodeIndex.get(nodeIndex) : undefined
    if (id) map.set(id, entryFor(object))
    else unassociated.push(object)
  })

  // Loaders that do not associate light nodes still emit them in document order.
  if (unassociated.length) {
    const remaining = lights.filter(light => !map.has(light.id))
    unassociated.forEach((object, index) => {
      if (remaining[index]) map.set(remaining[index].id, entryFor(object))
    })
  }
  return map
}

function entryFor(light) {
  return { light, baseIntensity: Number.isFinite(light.intensity) ? light.intensity : 0 }
}

function uniqueLightId(label, nodeIndex, usedIds) {
  const base = slugifyLightName(label) || `light-${nodeIndex}`
  let id = base
  let suffix = 2
  while (usedIds.has(id)) id = `${base}-${suffix++}`
  usedIds.add(id)
  return id
}

function slugifyLightName(label) {
  return String(label ?? '').trim().replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '')
}

/**
 * Applies the KHR_lights_punctual defaults and clamps to the spec's own range.
 *
 * The spec requires `0 <= innerConeAngle < outerConeAngle <= PI/2`; exporters do get this
 * wrong, so out-of-range values are pulled back into it here rather than passed on to a
 * renderer that would have to guess.
 */
function normalizeSpotCone(spot) {
  const rawOuter = Number(spot?.outerConeAngle)
  const rawInner = Number(spot?.innerConeAngle)

  // A missing, non-finite or zero-width outer cone is not a usable authored value, so it
  // falls back to the spec default rather than to a cone nothing can shine through.
  let outerConeAngle = Number.isFinite(rawOuter) && rawOuter > 0 ? rawOuter : DEFAULT_OUTER_CONE_ANGLE
  if (outerConeAngle > MAX_CONE_ANGLE) outerConeAngle = MAX_CONE_ANGLE

  const innerConeAngle = Number.isFinite(rawInner)
    ? Math.min(Math.max(rawInner, 0), outerConeAngle)
    : DEFAULT_INNER_CONE_ANGLE

  return { innerConeAngle, outerConeAngle }
}

function normalizeColor(color) {
  if (!Array.isArray(color) || color.length < 3) return [1, 1, 1]
  return color.slice(0, 3).map(channel => (Number.isFinite(channel) ? channel : 1))
}

function toArrayBuffer(source) {
  if (!source) return null
  if (source instanceof ArrayBuffer) return source
  if (ArrayBuffer.isView(source)) {
    return source.buffer.slice(source.byteOffset, source.byteOffset + source.byteLength)
  }
  return null
}
